import React, { useEffect, useCallback } from 'react';
import { Organization, Store, Device, Venue } from '../types';
import { SelectionStep, useSelection } from '../hooks/useSelection';
import { Organizations } from './Organizations';
import { Locations } from './Locations';
import {
  Devices,
  DevicesWithOverride,
  DevicesWithoutOverride,
} from './Devices';
import { SelectionStorage } from '../services/selectionService';

interface OnboardingScreenProps<T extends Device = Device> {
  onCompleted?: (organization: Organization, store: Store, device: T) => void;
  renderOrganizations?: (props: OrganizationStepProps) => React.ReactElement;
  renderLocations?: (props: LocationStepProps) => React.ReactElement;
  renderDevices?: (props: DeviceStepProps<T>) => React.ReactElement;
  renderLoading?: () => React.ReactElement;
  // Device configuration
  devicesConfig?:
    | Omit<DevicesWithOverride<T>, 'onSelectDevice' | 'onBack' | 'children'>
    | Omit<DevicesWithoutOverride<T>, 'onSelectDevice' | 'onBack' | 'children'>
    | undefined;
  // @param storage - Storage implementation for persisting selections
  storage: SelectionStorage;
}

export interface OrganizationStepProps {
  organizations: Organization[];
  loading: boolean;
  error?: string;
  onSelect: (org: Organization) => void;
  onRetry: () => void;
}

export interface LocationStepProps {
  venues: Venue[];
  filteredVenues: Venue[];
  loading: boolean;
  error?: string;
  searchTerm: string;
  onSelectStore: (venue: Venue, store: Store) => void;
  onBack: () => void;
  onRetry: () => void;
  onSearchChange: (term: string) => void;
}

export interface DeviceStepProps<T extends Device = Device> {
  devices: T[];
  filteredDevices: T[];
  loading: boolean;
  error?: string;
  searchTerm: string;
  onSelectDevice: (device: T) => void;
  onBack: () => void;
  onRetry: () => void;
  onSearchChange: (searchTerm: string) => void;
}

export const Onboarding = <T extends Device = Device>({
  onCompleted,
  renderOrganizations,
  renderLocations,
  renderDevices,
  renderLoading,
  devicesConfig,
  storage,
}: OnboardingScreenProps<T>): React.ReactElement => {
  const {
    selectOrganization: handleOrganizationSelect,
    selectStore: handleStoreSelect,
    selectDevice: handleDeviceSelect,
    changeOrganization,
    changeStore: handleDeviceBack,
    step,
    store,
    device,
    organization,
  } = useSelection<T>(storage);

  useEffect(() => {
    if (
      step === SelectionStep.COMPLETED &&
      onCompleted &&
      organization &&
      store &&
      device
    ) {
      onCompleted(organization, store, device);
    }
  }, [device, onCompleted, organization, step, store]);

  const renderCurrentStep = useCallback(() => {
    switch (step) {
      case SelectionStep.ORGANIZATION:
        return (
          <Organizations onSelect={handleOrganizationSelect}>
            {({ organizations, loading, error, onRetry }) => {
              if (renderOrganizations) {
                return renderOrganizations({
                  organizations,
                  loading,
                  error: error?.message,
                  onSelect: handleOrganizationSelect,
                  onRetry,
                });
              }
              return renderLoading?.() ?? null;
            }}
          </Organizations>
        );

      case SelectionStep.LOCATIONS:
        return (
          <Locations
            onSelectStore={handleStoreSelect}
            onBack={changeOrganization}
          >
            {({
              filteredVenues,
              loading,
              error,
              searchTerm,
              onSearchChange,
              onRetry,
              venues,
            }) => {
              if (renderLocations) {
                return renderLocations({
                  venues,
                  filteredVenues,
                  loading,
                  error: error?.message,
                  searchTerm,
                  onSelectStore: handleStoreSelect,
                  onBack: changeOrganization,
                  onRetry,
                  onSearchChange,
                });
              }
              return renderLoading?.() ?? null;
            }}
          </Locations>
        );

      case SelectionStep.DEVICE:
        return (
          <Devices<T>
            {...devicesConfig}
            onSelectDevice={handleDeviceSelect}
            onBack={handleDeviceBack}
          >
            {({
              devices,
              filteredDevices,
              loading,
              error,
              searchTerm,
              onSearchChange,
              onRetry,
            }) => {
              if (renderDevices) {
                return renderDevices({
                  devices: devices as T[],
                  filteredDevices: filteredDevices as T[],
                  loading,
                  error: error?.message,
                  searchTerm,
                  onSelectDevice: handleDeviceSelect,
                  onBack: handleDeviceBack,
                  onRetry,
                  onSearchChange,
                });
              }
              return renderLoading?.() ?? null;
            }}
          </Devices>
        );

      case SelectionStep.COMPLETED:
        return null;

      default:
        return renderLoading?.() ?? null;
    }
  }, [
    step,
    handleOrganizationSelect,
    handleStoreSelect,
    changeOrganization,
    handleDeviceSelect,
    renderLoading,
    renderOrganizations,
    renderLocations,
    renderDevices,
    handleDeviceBack,
    devicesConfig,
  ]);

  return <>{renderCurrentStep()}</>;
};
